import { Box } from '@mui/material';
import CircularProgress from '@mui/material/CircularProgress';
import type { Call } from '@twilio/voice-sdk';
import useTwilioDevice from 'packages/workspace/modules/prelude/useTwilioDevice';
import useSession from 'packages/workspace/services/store/session-store';
import { useCallback, useEffect, useState } from 'react';
import { MdCallEnd, MdMic, MdMicOff } from 'react-icons/md';
import ButtonWithIcon from '../common/ButtonWithIcon';
import Button from '@mui/material/Button';

/**
 * !Independant Component for Phone used in Dialog
 * @param param0
 */
interface IPhoneProps {
  token: string;
  phone: string;
  force_disconnect: boolean;
  client_phone: string;
  additionalParams?: Record<string, string | boolean>;
  call_message: string;
  call_complete_message: string;
}

const PhoneComponent: React.FC<IPhoneProps> = ({ token, phone, force_disconnect, client_phone, call_message, call_complete_message, additionalParams = {} }) => {
  const { deviceConnected, agentDevice, error } = useTwilioDevice(token);
  const [callCompleted, setCallCompleted] = useState<boolean>(false);
  const [callConnected, setCallConnected] = useState<boolean>(false);
  const [activeCall, setActiveCall] = useState<Call | null>(null);
  const [isMuted, setIsMuted] = useState<boolean>(false);
  const session = useSession();

  const disconnectCall = () => {
    agentDevice.disconnectAll();
  };

  useEffect(() => {
    return () => {
      disconnectCall();
    };
  }, []);

  useEffect(() => {
    if (force_disconnect) agentDevice.disconnectAll();
  }, [force_disconnect]);

  const muteUnMuteDevice = (mute: boolean) => {
    setIsMuted(mute);
  };
  useEffect(() => {
    async function makeCall() {
      const call_params = {
        params: {
          ...additionalParams,
          To: phone,
          oid: session.discoveryParams.organizationId,
          client: client_phone,
          acc_id: session?.iamPrincipal?.id,
        },
      };
      const call = await agentDevice.connect(call_params);

      call.on('disconnect', () => {
        setCallConnected(false);
        setCallCompleted(true);
      });
      call.on('accept', () => {
        setCallConnected(true);
      });

      call.on('reject', () => {
        setCallConnected(false);
        setCallCompleted(true);
      });

      setActiveCall(call);
    }
    if (deviceConnected && agentDevice !== undefined) {
      makeCall();
    }
  }, [deviceConnected, agentDevice]);

  const dialpad = [
    ['1', '2', '3'],
    ['4', '5', '6'],
    ['7', '8', '9'],
    ['*', '0', '#'],
  ];

  const sendDialCode = useCallback(
    (code) => {
      activeCall?.sendDigits(code);
    },
    [activeCall]
  );

  useEffect(() => {
    if (!activeCall) {
      return;
    }
    activeCall.mute(isMuted);
  }, [isMuted, activeCall]);

  if (deviceConnected && !callConnected && !callCompleted) return <span>{call_message}</span>;
  else if (deviceConnected && callConnected)
    return (
      <Box
        sx={{
          marginBlockStart: 2,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <div className="mt-4 flex flex-col mx-auto gap-2">
          {dialpad.map((row) => (
            <div className="flex gap-2" key={row.join('|')}>
              {row.map((number) => (
                <Button onClick={() => sendDialCode(number)} color="primary" variant="outlined" key={number} className="rounded-full">
                  {number}
                </Button>
              ))}
            </div>
          ))}
        </div>
        <Box
          component="div"
          sx={{
            display: 'flex',
            flexDirection: 'row',
            marginBlockStart: 2,
          }}
        >
          <ButtonWithIcon tooltip="End Call" Icon={MdCallEnd} onClick={() => disconnectCall()} size="large" className="text-2xl" />

          {isMuted ? (
            <ButtonWithIcon tooltip="Turn On Microphone" Icon={MdMicOff} onClick={() => muteUnMuteDevice(false)} size="large" className="text-2xl" />
          ) : (
            <ButtonWithIcon tooltip="Turn Off Microphone" Icon={MdMic} onClick={() => muteUnMuteDevice(true)} size="large" className="text-2xl" />
          )}
        </Box>
      </Box>
    );
  else if (callCompleted) return <span className="mt-5 block text-center">{call_complete_message}</span>;
  else if (error) return <span className="mt-5 block text-center">Something Went Wrong, Please Try Again</span>;
  return <CircularProgress aria-label="Phone Progress" />;
};

export default PhoneComponent;
